import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { User, BellRing, KeyRound, LogOut, ChevronDown, Settings as SettingsIcon, MonitorSmartphone } from 'lucide-react';
import NotificationPreferencesDialog from './NotificationPreferencesDialog';
import ChangePasswordDialog from './ChangePasswordDialog';
import KioskPinDialog from './KioskPinDialog';

/**
 * ProfileMenu — the top-right active dropdown that replaces the static name.
 *
 * Variants:
 *   - "desktop" : shows avatar + name + company in a single button (>=md)
 *   - "mobile"  : shows just the avatar (compact header on phones)
 *
 * All actions stay client-side until clicked, so the menu is cheap to mount.
 */
export default function ProfileMenu({ variant = 'desktop' }) {
  const { user, company, logout } = useAuth();
  const navigate = useNavigate();
  const [notifOpen, setNotifOpen] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [kioskPinOpen, setKioskPinOpen] = useState(false);

  if (!user) return null;

  const initials = (user.name || user.email || 'U').charAt(0).toUpperCase();
  const roleLabel =
    user.role === 'admin' ? 'Administrator'
    : user.role === 'supervisor' ? 'Supervisor'
    : 'Team Member';

  const goToProfile = () => navigate('/settings?section=profile');

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {variant === 'desktop' ? (
            <button
              data-testid="profile-menu-trigger"
              className="flex items-center gap-3 rounded-xl px-2 py-1 transition-colors hover:bg-white/5 focus:outline-none focus:ring-2 focus:ring-blue-500/40"
              aria-label="Open profile menu"
            >
              <div className="text-right hidden xl:block leading-tight">
                <p className="text-sm font-medium text-white">{user.name}</p>
                <p className="text-xs" style={{ color: '#64748B' }}>{company?.company_name}</p>
              </div>
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center overflow-hidden"
                style={{ background: '#1E88E5' }}
              >
                {user.profile_picture ? (
                  <img
                    src={user.profile_picture}
                    alt={user.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-white text-sm font-bold">{initials}</span>
                )}
              </div>
              <ChevronDown className="w-4 h-4 hidden sm:block" style={{ color: '#64748B' }} />
            </button>
          ) : (
            <button
              data-testid="profile-menu-trigger-mobile"
              className="w-9 h-9 rounded-full flex items-center justify-center overflow-hidden focus:outline-none focus:ring-2 focus:ring-blue-500/40"
              style={{ background: '#1E88E5' }}
              aria-label="Open profile menu"
            >
              {user.profile_picture ? (
                <img
                  src={user.profile_picture}
                  alt={user.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-white text-sm font-bold">{initials}</span>
              )}
            </button>
          )}
        </DropdownMenuTrigger>

        <DropdownMenuContent
          align="end"
          sideOffset={8}
          className="w-72 border border-[#2D3748] bg-[#0F1923] text-white"
          data-testid="profile-menu-content"
        >
          {/* Profile summary */}
          <DropdownMenuLabel className="px-3 py-3">
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0"
                style={{ background: '#1E88E5' }}
              >
                {user.profile_picture ? (
                  <img src={user.profile_picture} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-white text-base font-bold">{initials}</span>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-white truncate" data-testid="profile-menu-name">
                  {user.name}
                </p>
                <p className="text-xs truncate" style={{ color: '#94A3B8' }} data-testid="profile-menu-email">
                  {user.email}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <span
                    className="text-[10px] px-1.5 py-0.5 rounded-full font-medium"
                    style={{ background: 'rgba(30,136,229,0.15)', color: '#1E88E5' }}
                  >
                    {roleLabel}
                  </span>
                  {company?.company_name && (
                    <span className="text-[10px] truncate" style={{ color: '#64748B' }}>
                      · {company.company_name}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </DropdownMenuLabel>

          <DropdownMenuSeparator className="bg-[#2D3748]" />

          <DropdownMenuItem
            onClick={goToProfile}
            data-testid="profile-menu-edit-profile"
            className="flex items-center gap-2 px-3 py-2 text-sm text-white cursor-pointer focus:bg-white/5 focus:text-white hover:bg-white/5"
          >
            <User className="w-4 h-4" style={{ color: '#94A3B8' }} />
            <span>Edit Profile</span>
          </DropdownMenuItem>

          <DropdownMenuItem
            onClick={() => setNotifOpen(true)}
            data-testid="profile-menu-notif-prefs"
            className="flex items-center gap-2 px-3 py-2 text-sm text-white cursor-pointer focus:bg-white/5 focus:text-white hover:bg-white/5"
          >
            <BellRing className="w-4 h-4" style={{ color: '#94A3B8' }} />
            <span>Notification Preferences</span>
          </DropdownMenuItem>

          <DropdownMenuItem
            onClick={() => setPwdOpen(true)}
            data-testid="profile-menu-change-password"
            className="flex items-center gap-2 px-3 py-2 text-sm text-white cursor-pointer focus:bg-white/5 focus:text-white hover:bg-white/5"
          >
            <KeyRound className="w-4 h-4" style={{ color: '#94A3B8' }} />
            <span>Change Password</span>
          </DropdownMenuItem>

          <DropdownMenuItem
            onClick={() => setKioskPinOpen(true)}
            data-testid="profile-menu-kiosk-pin"
            className="flex items-center gap-2 px-3 py-2 text-sm text-white cursor-pointer focus:bg-white/5 focus:text-white hover:bg-white/5"
          >
            <MonitorSmartphone className="w-4 h-4" style={{ color: '#94A3B8' }} />
            <span>My Kiosk PIN</span>
          </DropdownMenuItem>

          <DropdownMenuItem
            onClick={() => navigate('/settings')}
            data-testid="profile-menu-settings"
            className="flex items-center gap-2 px-3 py-2 text-sm text-white cursor-pointer focus:bg-white/5 focus:text-white hover:bg-white/5"
          >
            <SettingsIcon className="w-4 h-4" style={{ color: '#94A3B8' }} />
            <span>All Settings</span>
          </DropdownMenuItem>

          <DropdownMenuSeparator className="bg-[#2D3748]" />

          <DropdownMenuItem
            onClick={logout}
            data-testid="profile-menu-logout"
            className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer focus:bg-red-500/10 focus:text-red-400 hover:bg-red-500/10"
            style={{ color: '#F87171' }}
          >
            <LogOut className="w-4 h-4" />
            <span>Sign Out</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <NotificationPreferencesDialog open={notifOpen} onOpenChange={setNotifOpen} />
      <ChangePasswordDialog open={pwdOpen} onOpenChange={setPwdOpen} />
      <KioskPinDialog open={kioskPinOpen} onOpenChange={setKioskPinOpen} />
    </>
  );
}
